import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { formatGermanDate, formatGermanDateTime } from '../lib/date-format';

type Status = 'offen' | 'verarbeitet' | 'abgewiesen';
type Kategorie = 'subrechnung' | 'lieferant';
type IngestQuelle = 'manuell' | 'mail';

interface BueroView {
  id: string;
  kategorie: Kategorie;
  ingestQuelle: IngestQuelle;
  auftragId: string | null;
  auftragTitel: string | null;
  lieferantName: string;
  rechnungsnummer: string | null;
  rechnungsdatum: string;
  betragBrutto: string | null;
  betragNetto: string | null;
  waehrung: string;
  notiz: string | null;
  originalFilename: string;
  sizeBytes: number;
  createdAt: string;
  authorDisplayName: string;
  portalUserId: string | null;
  uploadedByUserId: string | null;
  subunternehmerId: string | null;
  subunternehmerFirmenname: string | null;
  verarbeitetStatus: Status;
  verarbeitetAm: string | null;
  verarbeitetVon: string | null;
  verarbeitetNotiz: string | null;
  mailSubject: string | null;
  mailFrom: string | null;
  mailReceivedAt: string | null;
  externMessageId: string | null;
}

interface SubRef {
  id: string;
  firmenname: string;
}

export const Route = createFileRoute('/finanzen/eingangsrechnungen')({
  beforeLoad: () => requireAccess('/api/v1/eingangsrechnungen'),
  component: EingangsrechnungenPage,
});

function EingangsrechnungenPage() {
  const [kategorie, setKategorie] = useState<Kategorie | ''>('');
  const [ingestQuelle, setIngestQuelle] = useState<IngestQuelle | ''>('');
  const [status, setStatus] = useState<Status | ''>('offen');
  const [subId, setSubId] = useState<string>('');
  const [datumVon, setDatumVon] = useState<string>('');
  const [datumBis, setDatumBis] = useState<string>('');
  const [showUpload, setShowUpload] = useState(false);

  const subs = useQuery({
    queryKey: ['subunternehmer'],
    queryFn: () => api<{ items: SubRef[] }>('/api/v1/subunternehmer'),
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (kategorie) p.set('kategorie', kategorie);
    if (ingestQuelle) p.set('ingestQuelle', ingestQuelle);
    if (status) p.set('status', status);
    if (subId) p.set('subunternehmerId', subId);
    if (datumVon) p.set('rechnungsdatumVon', datumVon);
    if (datumBis) p.set('rechnungsdatumBis', datumBis);
    return p.toString();
  }, [kategorie, ingestQuelle, status, subId, datumVon, datumBis]);

  const list = useQuery({
    queryKey: ['eingangsrechnungen', queryString],
    queryFn: () =>
      api<{ items: BueroView[] }>(
        `/api/v1/eingangsrechnungen${queryString ? `?${queryString}` : ''}`,
      ),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Eingangsrechnungen"
        description="Posteingang aller eingehenden Rechnungen — von Subunternehmern hochgeladen (Subrechnung) oder vom Büro direkt (Lieferant). Verarbeiten oder abweisen."
        actions={
          <Button
            onClick={() => setShowUpload((s) => !s)}
            variant={showUpload ? 'ghost' : 'primary'}
          >
            {showUpload ? 'Abbrechen' : '+ Lieferanten-Rechnung hochladen'}
          </Button>
        }
      />

      {showUpload && (
        <LieferantUploadForm
          onDone={() => {
            setShowUpload(false);
          }}
        />
      )}

      <FilterBar
        kategorie={kategorie}
        setKategorie={setKategorie}
        ingestQuelle={ingestQuelle}
        setIngestQuelle={setIngestQuelle}
        status={status}
        setStatus={setStatus}
        subId={subId}
        setSubId={setSubId}
        datumVon={datumVon}
        setDatumVon={setDatumVon}
        datumBis={datumBis}
        setDatumBis={setDatumBis}
        subs={subs.data?.items ?? []}
      />

      <ErrorBanner error={list.error} />
      {list.isLoading && <div className="text-slate-500">Lade…</div>}

      {list.data && <RechnungenTable items={list.data.items} />}
    </div>
  );
}

function FilterBar(props: {
  kategorie: Kategorie | '';
  setKategorie: (k: Kategorie | '') => void;
  ingestQuelle: IngestQuelle | '';
  setIngestQuelle: (q: IngestQuelle | '') => void;
  status: Status | '';
  setStatus: (s: Status | '') => void;
  subId: string;
  setSubId: (id: string) => void;
  datumVon: string;
  setDatumVon: (s: string) => void;
  datumBis: string;
  setDatumBis: (s: string) => void;
  subs: SubRef[];
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Kategorie</label>
          <Select
            value={props.kategorie}
            onChange={(e) => props.setKategorie(e.target.value as Kategorie | '')}
          >
            <option value="">alle</option>
            <option value="subrechnung">Subrechnung</option>
            <option value="lieferant">Lieferant</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Quelle</label>
          <Select
            value={props.ingestQuelle}
            onChange={(e) => props.setIngestQuelle(e.target.value as IngestQuelle | '')}
          >
            <option value="">alle</option>
            <option value="manuell">manuell</option>
            <option value="mail">Mail</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
          <Select
            value={props.status}
            onChange={(e) => props.setStatus(e.target.value as Status | '')}
          >
            <option value="">alle</option>
            <option value="offen">offen</option>
            <option value="verarbeitet">verarbeitet</option>
            <option value="abgewiesen">abgewiesen</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Subunternehmer</label>
          <Select value={props.subId} onChange={(e) => props.setSubId(e.target.value)}>
            <option value="">alle</option>
            {props.subs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.firmenname}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Rechnung von</label>
          <Input
            type="date"
            value={props.datumVon}
            onChange={(e) => props.setDatumVon(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Rechnung bis</label>
          <Input
            type="date"
            value={props.datumBis}
            onChange={(e) => props.setDatumBis(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function LieferantUploadForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [lieferantName, setLieferantName] = useState('');
  const [rechnungsnummer, setRechnungsnummer] = useState('');
  const [rechnungsdatum, setRechnungsdatum] = useState('');
  const [betragBrutto, setBetragBrutto] = useState('');
  const [betragNetto, setBetragNetto] = useState('');
  const [waehrung, setWaehrung] = useState('EUR');
  const [notiz, setNotiz] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!pdf) throw new Error('Bitte PDF auswählen.');
      const fd = new FormData();
      fd.append('lieferantName', lieferantName);
      if (rechnungsnummer) fd.append('rechnungsnummer', rechnungsnummer);
      fd.append('rechnungsdatum', rechnungsdatum);
      fd.append('betragBrutto', betragBrutto);
      if (betragNetto) fd.append('betragNetto', betragNetto);
      if (waehrung) fd.append('waehrung', waehrung);
      if (notiz) fd.append('notiz', notiz);
      fd.append('pdf', pdf);
      const csrf = getCookie('busch_erp_csrf');
      const res = await fetch('/api/v1/eingangsrechnungen', {
        method: 'POST',
        credentials: 'same-origin',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;
        throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eingangsrechnungen'] });
      onDone();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    upload.mutate();
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-brand-200 bg-brand-50 p-4 shadow-sm space-y-3"
    >
      <h2 className="text-sm font-semibold text-slate-900">Lieferanten-Rechnung hochladen</h2>
      {error && <ErrorBanner error={new Error(error)} />}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Lieferant *</label>
          <Input
            value={lieferantName}
            onChange={(e) => setLieferantName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Rechnungsnummer</label>
          <Input value={rechnungsnummer} onChange={(e) => setRechnungsnummer(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Datum *</label>
          <Input
            type="date"
            value={rechnungsdatum}
            onChange={(e) => setRechnungsdatum(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Brutto *</label>
          <Input
            value={betragBrutto}
            onChange={(e) => setBetragBrutto(e.target.value)}
            placeholder="299,00"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Netto</label>
          <Input value={betragNetto} onChange={(e) => setBetragNetto(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Währung</label>
          <Input value={waehrung} onChange={(e) => setWaehrung(e.target.value.toUpperCase())} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">Notiz</label>
          <Input value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">PDF *</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setPdf(e.target.files?.[0] ?? null)}
            required
            className="block w-full text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={upload.isPending}>
          {upload.isPending ? 'Lade hoch…' : 'Hochladen'}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function RechnungenTable({ items }: { items: BueroView[] }) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
        Keine Eingangsrechnungen gefunden.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Kat.</th>
            <th className="px-4 py-2 text-left">Quelle</th>
            <th className="px-4 py-2 text-left">Lieferant</th>
            <th className="px-4 py-2 text-left">Auftrag</th>
            <th className="px-4 py-2 text-left">Datum</th>
            <th className="px-4 py-2 text-right">Brutto</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it) => (
            <RechnungRow key={it.id} item={it} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RechnungRow({ item }: { item: BueroView }) {
  const qc = useQueryClient();
  const [showStatusForm, setShowStatusForm] = useState(false);

  const setStatusMutation = useMutation({
    mutationFn: (vars: { status: Status; notiz?: string }) =>
      api<{ item: BueroView }>(`/api/v1/eingangsrechnungen/${item.id}/status`, {
        method: 'PATCH',
        body: vars,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eingangsrechnungen'] });
      setShowStatusForm(false);
    },
  });

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-2">
          <StatusBadge status={item.verarbeitetStatus} />
        </td>
        <td className="px-4 py-2">
          <KategorieBadge kategorie={item.kategorie} />
        </td>
        <td className="px-4 py-2 text-slate-900">
          {item.kategorie === 'subrechnung'
            ? (item.subunternehmerFirmenname ?? '—')
            : item.ingestQuelle === 'mail'
              ? '📧 Mail-Eingang'
              : 'Büro'}
          <div className="text-xs text-slate-500">
            {item.ingestQuelle === 'mail' ? (item.mailFrom ?? '—') : item.authorDisplayName}
          </div>
        </td>
        <td className="px-4 py-2 text-slate-900">{item.lieferantName}</td>
        <td className="px-4 py-2 text-slate-700 text-xs">{item.auftragTitel ?? '—'}</td>
        <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
          {formatDate(item.rechnungsdatum)}
        </td>
        <td className="px-4 py-2 text-right text-slate-900 tabular-nums">
          {formatBetrag(item.betragBrutto, item.waehrung)}
        </td>
        <td className="px-4 py-2 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <a
              href={`/api/v1/eingangsrechnungen/${item.id}/datei`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline text-xs font-medium"
            >
              PDF
            </a>
            {item.verarbeitetStatus === 'offen' ? (
              <Button
                variant="ghost"
                onClick={() => setShowStatusForm((s) => !s)}
                className="text-xs"
              >
                {showStatusForm ? 'Abbrechen' : 'Bearbeiten…'}
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setStatusMutation.mutate({ status: 'offen' })}
                className="text-xs"
                disabled={setStatusMutation.isPending}
              >
                Zurücksetzen
              </Button>
            )}
          </div>
        </td>
      </tr>
      {(item.notiz || item.verarbeitetNotiz || item.mailSubject) && (
        <tr className="bg-slate-50/50">
          <td></td>
          <td colSpan={7} className="px-4 pb-2 text-xs text-slate-600">
            {item.mailSubject && (
              <div>
                <span className="font-semibold">Mail-Betreff:</span> {item.mailSubject}
              </div>
            )}
            {item.notiz && (
              <div>
                <span className="font-semibold">Notiz:</span> {item.notiz}
              </div>
            )}
            {item.verarbeitetNotiz && (
              <div>
                <span className="font-semibold">Büro-Notiz:</span> {item.verarbeitetNotiz}
                {item.verarbeitetAm && (
                  <span className="text-slate-400"> · {formatDateTime(item.verarbeitetAm)}</span>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
      {showStatusForm && item.verarbeitetStatus === 'offen' && (
        <tr className="bg-slate-50">
          <td colSpan={8} className="px-4 py-3">
            <StatusForm
              onSubmit={(status, notiz) =>
                setStatusMutation.mutate(notiz === undefined ? { status } : { status, notiz })
              }
              pending={setStatusMutation.isPending}
              error={setStatusMutation.error}
              onCancel={() => setShowStatusForm(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function StatusForm(props: {
  onSubmit: (status: Status, notiz?: string) => void;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<Status>('verarbeitet');
  const [notiz, setNotiz] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = notiz.trim();
    if (trimmed) {
      props.onSubmit(status, trimmed);
    } else {
      props.onSubmit(status);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <ErrorBanner error={props.error} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[10rem_1fr_auto_auto] items-end">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Neuer Status</label>
          <Select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
            <option value="verarbeitet">verarbeitet</option>
            <option value="abgewiesen">abgewiesen</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Notiz (optional)</label>
          <Input
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="z.B. mit Rechnung 2026-RE-0042 verrechnet"
            maxLength={2000}
          />
        </div>
        <Button type="submit" disabled={props.pending}>
          {props.pending ? 'Speichere…' : 'Übernehmen'}
        </Button>
        <Button type="button" variant="ghost" onClick={props.onCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const styles: Record<Status, string> = {
    offen: 'bg-amber-100 text-amber-800',
    verarbeitet: 'bg-emerald-100 text-emerald-800',
    abgewiesen: 'bg-rose-100 text-rose-800',
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function KategorieBadge({ kategorie }: { kategorie: Kategorie }) {
  const styles: Record<Kategorie, string> = {
    subrechnung: 'bg-violet-100 text-violet-800',
    lieferant: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[kategorie]}`}>
      {kategorie === 'subrechnung' ? 'Sub' : 'Lief.'}
    </span>
  );
}

function getCookie(name: string): string | null {
  const parts = document.cookie.split(';').map((c) => c.trim());
  for (const part of parts) {
    if (part.startsWith(name + '=')) return decodeURIComponent(part.slice(name.length + 1));
  }
  return null;
}

const formatDate = formatGermanDate;
const formatDateTime = formatGermanDateTime;

function formatBetrag(s: string | null, waehrung: string): string {
  if (s === null) return '—';
  const n = Number.parseFloat(s);
  if (Number.isNaN(n)) return `${s} ${waehrung}`;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: waehrung,
    minimumFractionDigits: 2,
  }).format(n);
}
